import React, {useEffect, useState} from "react";
import {useVsiAppContext} from "../../../stores/VsiApp";
import {useSearchParams} from "react-router-dom";
import {useDataWithRoute, useTitleFromComponent} from "../../../utils/hooks";
import {VsiBubble, VsiH4} from "../../../styled/App.styled";
import {
    diskTypeWithFree,
    formatBarData
} from "../../../utils/chartUtils";
import StackedColumnChart from "../../../components/StackedColumnChart";
import Grid from "../../../components/Grid";
import VsiLabeledSelect from "../../../components/VsiLabeledSelect";
import {numberFormatter} from "../../../utils/gridFormatters";
import {Card} from "reactstrap";

const defs = [
    {
        "field": "device_name",
        "headerName": "Device Name"
    },
    {
        "field": "device_type_code",
        "headerName": "Device Type",
        "enableRowGroup": true
    },
    {
        "field": "total_free",
        "type": "numericColumn",
        "headerName": "Free",
        "aggFunc": "sum",
        "allowedAggFuncs": [
            "sum"
        ],
        "filter": "agNumberColumnFilter",
        "cellRenderer": numberFormatter,
    },
    {
        "field": "used_fibre",
        "type": "numericColumn",
        "headerName": "Fibre",
        "filter": "agNumberColumnFilter",
        "aggFunc": "sum",
        "allowedAggFuncs": [
            "sum"
        ],
        "cellRenderer": numberFormatter,
    },
    {
        "field": "used_sata",
        "type": "numericColumn",
        "headerName": "SATA",
        "filter": "agNumberColumnFilter",
        "aggFunc": "sum",
        "allowedAggFuncs": [
            "sum"
        ],
        "cellRenderer": numberFormatter,
    },
    {
        "field": "used_sas",
        "type": "numericColumn",
        "headerName": "SAS",
        "filter": "agNumberColumnFilter",
        "aggFunc": "sum",
        "allowedAggFuncs": [
            "sum"
        ],
        "cellRenderer": numberFormatter,
    },
    {
        "field": "used_ssd",
        "type": "numericColumn",
        "headerName": "SSD",
        "filter": "agNumberColumnFilter",
        "aggFunc": "sum",
        "allowedAggFuncs": [
            "sum"
        ],
        "cellRenderer": numberFormatter,
    },
    {
        "field": "used_unknown",
        "type": "numericColumn",
        "headerName": "Unknown",
        "aggFunc": "sum",
        "allowedAggFuncs": [
            "sum"
        ],
        "filter": "agNumberColumnFilter",
        "cellRenderer": numberFormatter,
    },
];

const allBus = [
    {value: 'device_name', label: 'Device'},
    {value: 'device_type_code', label: 'Device Type'},
];

const sortingArr = ['Free', 'Fibre', 'SATA', 'SAS', 'SSD', 'Unknown'];

export default function DiskTypeBy({context, title}) {
    useTitleFromComponent(`${context} / ${title}`);
    const { theme, client, periodId } = useVsiAppContext();

    const [searchParams, setSearchParams] = useSearchParams();
    const [allData, setAllData] = useState([]);

    const searchBu = searchParams.get('bu');
    let defaultBu = allBus[0];
    if (searchBu) {
        defaultBu = allBus.find(({value}) => searchBu === value) || defaultBu;
    }
    const [bu, setBu] = useState(defaultBu);
    const [barData, setBarData] = useState({
        categories: [],
        series: []
    });
    const [data] = useDataWithRoute(`/api/v6/report/enterprise_disk_type_by_${bu?.value}_proc?client_id=${client.clientId}&period_id=${periodId}`);

    useEffect(() => {
        const validData = data?.recordset || [];
        setAllData(validData);
        updateChartWithSelection(validData);
    }, [data]);

    const updateChartWithSelection = data => {
        setBarData(formatBarData(data || allData, {
            sortingArr,
            sortingField: bu.value,
            defs: defs.filter(def => sortingArr.includes(def.headerName))
        }));
    }

    const onUnitChange = (selectedUnit) => {
        setBu(selectedUnit);
        setSearchParams({
            bu: selectedUnit.value,
        }, {replace: true});
    };

    const filterChanged = (eventData) => {
        let filteredData = [];
        eventData.api.forEachNodeAfterFilter(node => {
            if(node.data) filteredData.push(node.data);
        });

        updateChartWithSelection(filteredData);
    };

    return <Card className={`report-card p-2 nav-card-${theme}`}>
        <VsiH4>Disk Type by Device</VsiH4>
        <div className={"w-100 p-2 d-flex"}>
            <VsiLabeledSelect options={allBus} label={'Select Unit'} onChange={onUnitChange} defaultValue={bu}/>
        </div>
        <VsiBubble className={"mb-2"} unit={bu.value}>
            <StackedColumnChart categories={barData.categories}
                                colors={diskTypeWithFree}
                                height={600}
                                series={barData.series}/>
        </VsiBubble>
        <VsiBubble unit={bu.value}>
            <Grid data={allData} columnDefs={defs} filterChanged={filterChanged}/>
        </VsiBubble>
    </Card>
}
